import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { SHELL, MAX, CARD, BUTTON, brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import StorefrontNav from './StorefrontNav';
import { ShellMessage, Row } from './shellHelpers';

/**
 * Post-checkout thank-you page. Stripe (or the direct flow) lands here
 * with the order id — we fetch the customer's own order record and
 * show a quick summary + next-step buttons (View orders / Print receipt /
 * Keep shopping).
 */
export default function StorefrontOrderConfirm() {
  const { orderId } = useParams();
  const { brand, error } = useBrand();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const accent = brand?.brand_color || '#422AFB';

  useEffect(() => {
    if (!brand?._id || !orderId) return;
    setLoading(true);
    StorefrontAPI.getMyOrder(brand._id, orderId)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [brand?._id, orderId]);

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        {loading && <div style={{ color: '#64748b' }}>Loading…</div>}
        {!loading && !order && <div style={{ color: '#64748b' }}>Order not found.</div>}
        {!loading && order && (
          <div style={{ ...CARD, maxWidth: 720, margin: '0 auto' }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>Thanks — your order is in.</div>
            <div style={{ color: '#64748b', marginBottom: 18 }}>
              Order <strong>{order.number}</strong> placed {new Date(order.placed_at).toLocaleString()}.
            </div>
            <div style={{
              padding: 12, background: '#f8fafc', borderRadius: 10, marginBottom: 18,
              fontSize: 13, color: '#475569',
            }}>
              Status: <strong>{order.status}</strong>. You'll get an email once it's confirmed and shipped.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {(order.line_items || []).map((li, i) => (
                  <tr key={i}>
                    <td style={{ padding: '6px 0' }}>
                      <div style={{ fontWeight: 600 }}>{li.product_name}</div>
                      {li.variant_name && <div style={{ fontSize: 12, color: '#64748b' }}>{li.variant_name}</div>}
                    </td>
                    <td style={{ padding: '6px 0', textAlign: 'right' }}>× {li.quantity}</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>
                      {formatMoney(li.line_total, order.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 10, paddingTop: 10 }}>
              <Row label='Subtotal'>{formatMoney(order.subtotal, order.currency)}</Row>
              <Row label='Tax'>{formatMoney(order.tax_total, order.currency)}</Row>
              <Row label='Shipping'>{formatMoney(order.shipping_total, order.currency)}</Row>
              <Row label='Total'><strong>{formatMoney(order.grand_total, order.currency)}</strong></Row>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
              <button onClick={() => navigate(brandPath(brand, '/account'))} style={BUTTON(accent)}>
                View my orders
              </button>
              <button onClick={() => navigate(brandPath(brand, `/order/${order._id}/receipt`))} style={BUTTON(accent, true)}>
                Print receipt
              </button>
              <button onClick={() => navigate(brandPath(brand, '/shop'))} style={BUTTON(accent, true)}>
                Keep shopping
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
